import React, { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { CalendarIcon, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Calendar } from '@/components/ui/calendar'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { quotationSchema, QuotationFormValues, QuotationItemFormValues } from './schema'
import { QuotationResponse } from '@/app/types/quotation'
import { Site } from '@/app/types/site'
import { Item } from '@/app/types/item'
import { Godown } from '@/app/types/inventory'
import { EditableDataGrid, GridColumn, ProductSearchResult } from '@/app/pages/sales-invoices/components/EditableDataGrid'
import { PartySelector } from '@/app/components/common/PartySelector'
import { Card, CardContent } from '@/components/ui/card'
import { getFinalItemPriceDetails } from '@/app/lib/salePriceRuleUtils'
import { Decimal } from 'decimal.js'
import { usePreferenceStore } from '@/app/store/preferenceStore'
import { getItemPriceDetailsWithFallback, isSQLiteAvailable } from '@/lib/indexeddb/integrationUtils'

interface QuotationFormProps {
  quotation?: QuotationResponse
  sites: Site[]
  godowns: Godown[]
  isSubmitting: boolean
  onSubmit: (data: QuotationFormValues) => void
  searchItems: (query: string) => Promise<Item[]>
  isLoadingItems: boolean
  isDisabled?: boolean
}

export function QuotationForm({
  quotation,
  sites,
  godowns,
  isSubmitting,
  onSubmit,
  searchItems,
  isLoadingItems,
  isDisabled = false,
}: QuotationFormProps): React.ReactElement {
  const isEditMode = !!quotation
  const gridRef = useRef<HTMLDivElement>(null)
  
  // Get user preferences
  const { defaultSiteId, defaultGodownId: preferenceGodownId } = usePreferenceStore()
  
  // Default warehouse for all items
  const [defaultGodownId, setDefaultGodownId] = useState<number>(
    godowns.length > 0 ? (
      // Use preference godown if available, otherwise first godown
      preferenceGodownId && godowns.some(g => g.id === preferenceGodownId) 
        ? preferenceGodownId 
        : godowns[0].id
    ) : 0
  )
  
  // Initialize form with default values or existing quotation data
  const form = useForm<QuotationFormValues>({
    resolver: zodResolver(quotationSchema),
    defaultValues: quotation
      ? {
          party_id: quotation.party_id,
          site_id: quotation.site_id,
          transaction_date: new Date(quotation.transaction_date),
          valid_until_date: quotation.valid_until_date ? new Date(quotation.valid_until_date) : null,
          reference_number: quotation.reference_number || '',
          terms_and_conditions: quotation.terms_and_conditions || '',
          notes: quotation.notes || '',
          total_amount: quotation.total_amount || 0,
          items: quotation.items.map((item) => ({
            item_id: item.item_id,
            godown_id: item.godown_id || defaultGodownId,
            item_name: item.item_name || item.description || `Item ID: ${item.item_id}`,
            item_code: item.item_code || '',
            description: item.description || '',
            quantity: Number(item.quantity),
            rate: Number(item.rate),
            discount_percent: Number(item.discount_percent || 0),
            discount_amount: Number(item.discount_amount || 0),
            amount: Number(item.amount || 0),
            tax_rate_id: item.tax_rate_id || null,
            taxable_amount: Number(item.taxable_amount || 0),
            tax_amount: Number(item.tax_amount || 0),
          })),
        }
      : {
          party_id: undefined,
          site_id:
            defaultSiteId && sites.some((s) => s.id === defaultSiteId)
              ? defaultSiteId
              : undefined,
          transaction_date: new Date(),
          valid_until_date: null,
          reference_number: '',
          terms_and_conditions: '',
          notes: '',
          total_amount: 0,
          items: [],
        },
  });

  // State for line items - initialize with empty row if no items
  const [items, setItems] = useState<QuotationItemFormValues[]>(() => {
    const formItems = form.getValues().items || []
    // If no items, create a default empty row
    if (formItems.length === 0) {
      return [{
        item_id: 0,
        item_name: '',
        item_code: '',
        description: '',
        quantity: 1,
        rate: 0,
        discount_percent: 0,
        discount_amount: 0,
        amount: 0,
        godown_id: defaultGodownId,
        tax_rate_id: null,
        taxable_amount: 0,
        tax_amount: 0,
      }]
    }
    return formItems
  })

  // Wrapper for searchItems to convert Item[] to expected format
  const searchProductsWrapper = async (searchTerm: string): Promise<ProductSearchResult[]> => {
    if (!searchTerm || searchTerm.length < 2) return []
    
    try {
      const itemResults = await searchItems(searchTerm)
      return itemResults.map(item => ({
        id: item.id,
        name: item.name || '',
        code: item.code || null,
        description: item.description || item.name || null,
        tax_rate_id: item.tax_rate_id || null,
        // Store base_unit_id in the search result for later use
        base_unit_id: item.base_unit_id,
      } as ProductSearchResult & { base_unit_id?: number }))
    } catch (error) {
      console.error('Error searching products:', error)
      return []
    }
  }

  // Handle product selection from search modal - matching sales invoice implementation
  const handleProductSelect = async (product: ProductSearchResult, rowIndex: number): Promise<void> => {
    console.log('[QuotationForm] Product selected:', product);
    
    // Get the current quantity for the row (default to 1 if not set)
    const currentRow = items[rowIndex] || {};
    const currentQuantity = new Decimal(String(currentRow.quantity || 1));
    const currentSiteId = form.getValues('site_id');
    const currentPartyId = form.getValues('party_id');
    
    // Tax issue tracking - product selection with tax_rate_id
    console.log(`[TAX DEBUG] Product selection with tax_rate_id: ${product.tax_rate_id}`);

    let priceDetails: any | null = null;
    try {
      // Tax issue tracking - calling price details with tax info
      console.log("[TAX DEBUG] Calling price details with tax_rate_id:", product.tax_rate_id);
      
      // Check if SQLite is available
      if (isSQLiteAvailable()) {
        console.log("[SQLite] Using local pricing calculation");
        priceDetails = await getItemPriceDetailsWithFallback(
          product.id,
          currentQuantity.toNumber(),
          currentSiteId,
          currentPartyId,
          undefined, // priceListId
          product.tax_rate_id,
          // Fallback to API if SQLite fails
          async (itemId, quantity) => getFinalItemPriceDetails(
            itemId,
            undefined,
            currentSiteId,
            currentPartyId,
            quantity,
            product.tax_rate_id
          )
        );
      } else {
        // Use API directly if SQLite not available
        priceDetails = await getFinalItemPriceDetails(
          product.id,
          undefined, // Let the function fetch purchase price if needed by a rule
          currentSiteId,
          currentPartyId,
          currentQuantity.toNumber(),
          product.tax_rate_id // Pass the tax_rate_id directly from the product
        );
      }
      
      // Tax issue tracking - received price details
      console.log("[TAX DEBUG] Received priceDetails with tax info:", {
        tax_rate_id: priceDetails ? priceDetails.tax_rate_id : null,
        tax_amount: priceDetails ? priceDetails.tax_amount : null,
        final_amount: priceDetails ? priceDetails.final_amount : null
      });
      
    } catch (error) {
      console.error("[QuotationForm] Error fetching final price details:", error);
      toast.error(`Error calculating price for ${product.name}: ${(error as Error).message}`);
      priceDetails = null;
    }

    // Update the grid data state
    setItems(currentItems => {
      const newItems = [...currentItems];
      if (newItems[rowIndex]) {
        // Start with existing row data and basic product info
        const updatedRow = {
          ...newItems[rowIndex],
          item_id: product.id,
          item_name: product.name,
          item_code: product.code || '',
          description: product.description || product.name || '',
          godown_id: newItems[rowIndex].godown_id || defaultGodownId,
          quantity: currentQuantity.toNumber(),
          unit_id: (product as any).base_unit_id, // Store unit_id from the product
          // Apply fetched price details
          rate: priceDetails ? priceDetails.rate.toNumber() : 0,
          discount_percent: priceDetails ? priceDetails.discount_percent.toNumber() : 0,
          discount_amount: priceDetails ? priceDetails.discount_amount.toNumber() : 0,
          taxable_amount: priceDetails ? priceDetails.taxable_amount.toNumber() : 0,
          tax_rate_id: priceDetails ? priceDetails.tax_rate_id : null,
          tax_amount: priceDetails ? priceDetails.tax_amount.toNumber() : 0,
          amount: priceDetails ? priceDetails.final_amount.toNumber() : 0,
          applied_sale_rule_id: priceDetails ? priceDetails.applied_sale_rule_id : null, // Store applied rule ID
        };

        // Tax issue tracking - row update with tax info
        console.log(`[TAX DEBUG] Updated row ${rowIndex} tax data:`, {
          tax_rate_id: updatedRow.tax_rate_id,
          taxable_amount: updatedRow.taxable_amount,
          tax_amount: updatedRow.tax_amount,
          final_amount: updatedRow.amount
        });

        newItems[rowIndex] = updatedRow;
      }
      return newItems;
    });

    // Focus is handled by EditableDataGrid's handleSelectItem after state update
  };

  // Handle grid data changes
  const handleGridChange = (newData: Record<string, unknown>[]): void => {
    const typedData = newData as QuotationItemFormValues[];
    setItems(typedData);
    form.setValue('items', typedData);
    
    // Calculate and update total
    const total = typedData.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    form.setValue('total_amount', total);
  };

  // Handle quantity change for a row with applied sale rule
  const handleQuantityChangeForRow = async (rowIndex: number, newQuantity: number): Promise<void> => {
    // Tax issue tracking - quantity change with potential tax implications
    console.log(`[TAX DEBUG] Quantity changed for row ${rowIndex} to ${newQuantity}. Re-evaluating price with tax.`);

    const itemInRow = items[rowIndex];
    if (!itemInRow) {
      console.warn(`[QuotationForm] Can't update quantity: No item found at row ${rowIndex}`);
      return;
    }

    const itemId = Number(itemInRow.item_id);
    if (!itemId) {
      console.warn(`[QuotationForm] Can't update quantity: No valid item_id in row ${rowIndex}`);
      return;
    }

    const currentSiteId = form.getValues('site_id');
    const currentPartyId = form.getValues('party_id');
    // Get current tax_rate_id to ensure it's used in the calculation
    const currentTaxRateId = Number(itemInRow.tax_rate_id) || null;
    const hasAppliedRule = Boolean(itemInRow.applied_sale_rule_id);

    try {
      console.log(`[QuotationForm] Recalculating price for item ${itemId} with quantity ${newQuantity}, has rule: ${hasAppliedRule}`);

      let priceDetails;
      
      // Check if SQLite is available for quantity change pricing
      if (isSQLiteAvailable()) {
        console.log("[SQLite] Using local pricing for quantity change");
        priceDetails = await getItemPriceDetailsWithFallback(
          itemId,
          newQuantity,
          currentSiteId,
          currentPartyId,
          undefined, // priceListId
          currentTaxRateId,
          // Fallback to API if SQLite fails
          async (id, qty) => getFinalItemPriceDetails(
            id,
            undefined,
            currentSiteId,
            currentPartyId,
            qty,
            currentTaxRateId
          )
        );
      } else {
        priceDetails = await getFinalItemPriceDetails(
          itemId,
          undefined, // Let rule util fetch purchase price if needed
          currentSiteId,
          currentPartyId,
          newQuantity, // Use the new quantity
          currentTaxRateId // Pass the current tax_rate_id to maintain it
        );
      }

      if (!priceDetails) {
        console.error(`[QuotationForm] Failed to get price details for item ${itemId}`);
        toast.error(`Failed to recalculate price for item.`);
        return;
      }

      // Tax issue tracking - received updated price details after quantity change
      console.log(`[TAX DEBUG] Re-calculated price details with tax for item ${itemId}:`, {
        rate: priceDetails.rate.toNumber(),
        tax_rate_id: priceDetails.tax_rate_id,
        tax_amount: priceDetails.tax_amount.toNumber(),
        final_amount: priceDetails.final_amount.toNumber(),
        source: priceDetails.source,
        applied_rule_id: priceDetails.applied_sale_rule_id
      });

      setItems(currentItems => {
        const newItems = [...currentItems];
        if (newItems[rowIndex]) {
          // Only update the pricing-related fields, not the quantity
          // The quantity is already updated in the EditableDataGrid
          newItems[rowIndex] = {
            ...newItems[rowIndex],
            // Don't override quantity here as it's already set by EditableDataGrid
            rate: priceDetails.rate.toNumber(),
            discount_percent: priceDetails.discount_percent.toNumber(),
            discount_amount: priceDetails.discount_amount.toNumber(),
            taxable_amount: priceDetails.taxable_amount.toNumber(),
            tax_rate_id: priceDetails.tax_rate_id,
            tax_amount: priceDetails.tax_amount.toNumber(),
            amount: priceDetails.final_amount.toNumber(),
            applied_sale_rule_id: priceDetails.applied_sale_rule_id,
          };

          console.log(`[QuotationForm] Updated row ${rowIndex} after quantity change - amount: ${priceDetails.final_amount.toNumber()}`);
        }
        return newItems;
      });
    } catch (error) {
      console.error(`[QuotationForm] Error re-fetching price details for item ${itemId} on quantity change:`, error);
      toast.error(`Error recalculating price: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };
  
  const { control, handleSubmit, watch, setValue, reset, formState: { errors } } = form;
  const currentSiteId = watch('site_id');
  
  // Add useEffect to set default site when sites have loaded and no site is selected
  useEffect(() => {
    // Set default site only in CREATE mode when sites have loaded and no site is yet selected
    if (!quotation && !isSubmitting && sites && sites.length > 0 && !currentSiteId) {
      setValue('site_id', sites[0].id, { shouldValidate: true, shouldDirty: true });
    }
  }, [quotation, isSubmitting, sites, currentSiteId, setValue]);

  // Update form when items change
  useEffect(() => {
    // Filter out empty rows before setting form value to avoid validation errors
    const validItems = items.filter(item => item.item_id && item.item_id > 0);
    form.setValue("items", validItems, { shouldValidate: true });
  }, [items, form]);

  // Handle form submission
  const handleFormSubmit = (data: QuotationFormValues): void => {
    console.log('Form submitted data:', data);
    
    // Filter out empty rows (items without item_id)
    const validItems = data.items.filter(item => item.item_id && item.item_id > 0);
    
    // Check if we have at least one valid item
    if (validItems.length === 0) {
      toast.error('Please add at least one product');
      return;
    }
    
    // Ensure all line items have total calculated
    const updatedItems = validItems.map(item => {
      console.log('Processing item for submission:', item);
      
      // Ensure the required fields are present for API
      return {
        ...item,
        // Make sure unit_id is set if we have one (if needed by API)
        // Note: unit_id is not in our schema, so skip this unless needed
        total_amount: item.amount, // Use amount field instead of total_amount
        // Explicitly ensure description is included
        description: item.description || ''
      };
    });
    
    console.log('Updated items with descriptions for API:', updatedItems);
    
    // Make sure transaction_date is set properly
    const apiData = {
      ...data,
      items: updatedItems
    };
    
    onSubmit(apiData);
  };

  // Define columns for the items data grid - matching sales invoice structure
  const columns: GridColumn[] = React.useMemo(() => [
    { field: 'item_id', header: 'Product', type: 'product', width: '200px' },
    { field: 'quantity', header: 'Qty', type: 'number', width: '80px' },
    { field: 'rate', header: 'Rate', type: 'price', width: '100px' },
    { field: 'discount_percent', header: 'Disc %', type: 'number', width: '80px' },
    // Read-only calculated fields populated by the pricing util
    { field: 'taxable_amount', header: 'Taxable Amt', type: 'price', editable: false, width: '120px' },
    { field: 'tax_amount', header: 'Tax Amt', type: 'price', editable: false, width: '100px' },
    { field: 'amount', header: 'Amount', type: 'price', editable: false, width: '120px' },
    { field: 'description', header: 'Description', type: 'text', width: '180px' },
    { field: 'actions', header: '', type: 'actions', width: '50px' },
  ], []);

  // Calculate the total amount of the quotation (exclude empty rows)
  const totalAmount = items
    .filter(item => item.item_id && item.item_id > 0)
    .reduce((sum, item) => {
      return sum + Number(item.amount || 0);
    }, 0).toFixed(2);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Party/Customer Selection */}
          <FormField
            control={form.control}
            name="party_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Customer *</FormLabel>
                <FormControl>
                  <PartySelector
                    value={field.value}
                    onChange={(partyId) => field.onChange(partyId)}
                    disabled={isDisabled || isSubmitting}
                    placeholder="Select or create customer"
                    filterPartyType="customer"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Site Selection */}
          <FormField
            control={form.control}
            name="site_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Site *</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(Number(value))}
                  value={field.value?.toString()}
                  disabled={isSubmitting}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a site" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {sites.map((site) => (
                      <SelectItem key={site.id} value={site.id.toString()}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Transaction Date */}
          <FormField
            control={form.control}
            name="transaction_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Date *</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isDisabled || isSubmitting}
                      >
                        {field.value ? (
                          format(new Date(field.value), "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Valid Until Date */}
          <FormField
            control={form.control}
            name="valid_until_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Valid Until Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isDisabled || isSubmitting}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) =>
                        date < new Date() || date > new Date("2100-01-01")
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Reference Number */}
          <FormField
            control={form.control}
            name="reference_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reference Number</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isDisabled || isSubmitting} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

        </div>

        {/* Default Warehouse Selection */}
        <div className="mb-4">
          <FormLabel>Default Warehouse</FormLabel>
          <Select
            value={defaultGodownId.toString()}
            onValueChange={(value) => setDefaultGodownId(Number(value))}
            disabled={isDisabled || isSubmitting}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select default warehouse" />
            </SelectTrigger>
            <SelectContent>
              {godowns.map((godown) => (
                <SelectItem key={godown.id} value={godown.id.toString()}>
                  {godown.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Terms and Conditions */}
        <FormField
          control={form.control}
          name="terms_and_conditions"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Terms and Conditions</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter terms and conditions here"
                  className="resize-none"
                  {...field}
                  value={field.value || ""}
                  disabled={isDisabled || isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Notes */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter any additional notes here"
                  className="resize-none"
                  {...field}
                  value={field.value || ""}
                  disabled={isDisabled || isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Line Items */}
        <Card>
          <CardContent className="p-0">
            <div className="space-y-2">
              <h3 className="text-lg font-medium p-6 pb-0">Line Items *</h3>
              <div ref={gridRef}>
                <EditableDataGrid
                  columns={columns}
                  data={items as unknown as Record<string, unknown>[]}
                  setData={setItems as unknown as React.Dispatch<React.SetStateAction<Record<string, unknown>[]>>}
                  productSearchFn={searchProductsWrapper}
                  isLoadingProducts={isLoadingItems}
                  disabled={isDisabled || isSubmitting}
                  onProductSelect={handleProductSelect}
                  onChange={handleGridChange}
                  siteId={form.watch('site_id')}
                  onQuantityChangeForRow={handleQuantityChangeForRow}
                />
              </div>
              {form.formState.errors.items && (
                <p className="text-sm font-medium text-destructive px-6">
                  {form.formState.errors.items.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Add debug info during development */}
        <div className="hidden">
          <p>Debug info:</p>
          <pre className="text-xs">
            {
              JSON.stringify(
                items.map((item) => ({
                  item_id: item.item_id,
                  description: item.description || ''
                })),
                null,
                2
              )
            }
          </pre>
        </div>

        {/* Order Total */}
        <div className="flex justify-end">
          <div className="text-right">
            <p className="text-sm text-muted-foreground">Total Amount</p>
            <p className="text-2xl font-medium">₹{totalAmount}</p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>{quotation ? "Update" : "Create"} Quotation</>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}